import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { decodeCsv, detectDelimiter, ORDER_FIELDS, parseCsv, PRODUCT_FIELDS, suggestMapping } from "@logiful/domain";
import { AlertTriangle, CheckCircle2, Download, FileUp, Save, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { ShippingMethodSelect } from "@/components/ShippingMethodSelect";
import { Badge, Button, cx, Input } from "@/components/ui";
import { api, ApiError, isDemo, type CsvImportBody, type CsvTemplate, type OrderCsvPreview, type ProductCsvPreview } from "@/lib/api";
import { fmtNumber } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";
type Kind = "orders" | "products";

/** Felder mit Standardwert (gilt, wenn die Spalte fehlt oder die Zelle leer ist) und Art des Eingabefelds */
const DEFAULTABLE: Record<Kind, Record<string, "text" | "number" | "country" | "yesno" | "method">> = {
  orders: { quantity: "number", country: "country", shippingMethodId: "method", note: "text" },
  products: { manufacturer: "text", countryOfOrigin: "country", hsCode: "text", customsValue: "number", grossWeightKg: "number", batchManaged: "yesno", bestBeforeManaged: "yesno" },
};
const LABEL = { orders: { title: msg("Aufträge per CSV importieren"), what: "Aufträge" }, products: { title: msg("Artikel per CSV importieren"), what: "Artikel" } };

/**
 * CSV-Import in drei Schritten: Datei + Vorlage → Spalten zuordnen (Vorschlag aus den Überschriften) → Vorschau mit
 * Fehlern je Zeile → importieren. Zuordnungen lassen sich als Vorlage speichern (z. B. „ERP-Export“).
 */
export function CsvImportDialog({ kind, open, onOpenChange, onDone }: { kind: Kind; open: boolean; onOpenChange: (o: boolean) => void; onDone?: () => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const fields = kind === "orders" ? ORDER_FIELDS : PRODUCT_FIELDS;
  const templates = useQuery({ queryKey: ["csv-templates", tenantId, kind], queryFn: () => api.imports.templates(tenantId, kind), enabled: open });
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [defaults, setDefaults] = useState<Record<string, string>>(kind === "orders" ? { country: "DE" } : {});
  const [templateId, setTemplateId] = useState<string>("auto");
  const [saveAs, setSaveAs] = useState("");
  const [transfer, setTransfer] = useState(true);
  const [preview, setPreview] = useState<OrderCsvPreview | ProductCsvPreview | null>(null);

  const rows = useMemo(() => (file ? parseCsv(file.text) : []), [file]);
  const header = rows[0] ?? [];
  const delimiter = file ? detectDelimiter(file.text) : ";";

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setTemplateId("auto"); setSaveAs(""); };
  const loadFile = async (f: File) => {
    const text = decodeCsv(new Uint8Array(await f.arrayBuffer()));
    setFile({ name: f.name, text });
    setPreview(null);
    const head = parseCsv(text)[0] ?? [];
    // gespeicherte Vorlage, deren Spalten alle vorhanden sind, sonst Vorschlag aus den Überschriften
    const fit = templates.data?.find((tpl) => Object.values(tpl.mapping).every((c) => head.some((h) => h.toLowerCase() === c.toLowerCase())));
    if (fit) { setTemplateId(fit.id); setMapping(fit.mapping); setDefaults(fit.defaults); }
    else { setTemplateId("auto"); setMapping(suggestMapping(kind, head)); }
  };
  const chooseTemplate = (id: string) => {
    setTemplateId(id);
    setPreview(null);
    const tpl = templates.data?.find((x) => x.id === id);
    if (tpl) { setMapping(tpl.mapping); setDefaults(tpl.defaults); } else setMapping(suggestMapping(kind, header));
  };

  const body = (): CsvImportBody => ({ csv: file!.text, fileName: file!.name, template: { kind, hasHeader: true, delimiter: null, mapping, defaults }, ...(kind === "products" ? { transfer } : {}) });
  // Pflichtfeld erfüllt durch Spalte oder Standardwert
  const missingRequired = Object.entries(fields).filter(([k, f]) => f.required && !mapping[k] && !defaults[k]).map(([, f]) => f.label);

  const check = useMutation({
    mutationFn: () => (kind === "orders" ? api.imports.previewOrders(tenantId, body()) : api.imports.previewProducts(tenantId, body())),
    onSuccess: setPreview,
    onError: (e) => toast.error("Prüfung fehlgeschlagen", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const run = useMutation({
    mutationFn: async () => {
      if (saveAs.trim()) {
        await api.imports.saveTemplate(tenantId, { kind, name: saveAs.trim(), hasHeader: true, delimiter: null, mapping, defaults });
        void qc.invalidateQueries({ queryKey: ["csv-templates", tenantId, kind] });
      }
      return kind === "orders" ? api.imports.importOrders(tenantId, body()) : api.imports.importProducts(tenantId, body());
    },
    onSuccess: (r) => {
      if ("duplicates" in r) toast.success(`${fmtNumber(r.created)} Aufträge importiert`, { description: [r.duplicates ? `${r.duplicates} schon vorhanden (übersprungen)` : "", r.unknownSkus.length ? `${r.unknownSkus.length} unbekannte SKU – betroffene Aufträge werden zurückgehalten` : ""].filter(Boolean).join(" · ") || "Sie werden jetzt geprüft und ans Lager übergeben." });
      else toast.success(`${fmtNumber(r.created)} Artikel angelegt, ${fmtNumber(r.updated)} aktualisiert`, { description: r.blocked.length ? `${r.blocked.length} nicht geändert (Charge/MHD gesperrt, solange das Lager Bestand hat)` : undefined });
      onDone?.();
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error("Import fehlgeschlagen", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });

  const p = preview;
  const count = p ? ("orders" in p ? p.orders : p.create + p.update) : 0;
  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content aria-describedby={undefined} className="fixed z-50 inset-x-4 top-[5vh] mx-auto max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-surface shadow-2xl animate-pop-in">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0">
              <Dialog.Title className="text-[17px] font-semibold">{t(LABEL[kind].title)}</Dialog.Title>
              <p className="text-fg-muted mt-0.5 text-[13px]">{t("Unsere Vorlage oder ein eigener Export (z. B. aus dem ERP) – die Spalten ordnest du einmal zu und speicherst sie als Vorlage.")}</p>
            </div>
            <a href={isDemo ? undefined : api.imports.downloadUrl(tenantId, kind)} className="ml-auto shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-border text-[12.5px] font-medium hover:bg-surface-2"><Download className="size-3.5" /> {t("Vorlage (CSV)")}</a>
            <Dialog.Close className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* 1. Datei */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) void loadFile(f); }}
              onClick={() => fileRef.current?.click()}
              className={cx("rounded-xl border border-dashed p-5 text-center cursor-pointer hover:bg-surface-2/50", file ? "border-border" : "border-border-strong")}
            >
              <input ref={fileRef} type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void loadFile(f); e.target.value = ""; }} />
              <FileUp className="size-5 mx-auto text-fg-subtle" />
              {file ? (
                <p className="mt-1 text-[13px]"><b>{file.name}</b> · {fmtNumber(Math.max(0, rows.length - 1))} {t("Zeilen · Trennzeichen „")}{delimiter === "\t" ? t("Tab") : delimiter}“ <span className="text-fg-muted">{t("– klicken für andere Datei")}</span></p>
              ) : (
                <p className="mt-1 text-[13px] text-fg-muted">{t("CSV-Datei hierher ziehen oder klicken (Excel: „Speichern unter → CSV“)")}</p>
              )}
            </div>

            {/* 2. Zuordnung */}
            {file && (
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Spalten zuordnen")}</span>
                  <select value={templateId} onChange={(e) => chooseTemplate(e.target.value)} className="ml-auto h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label={t("Vorlage")}>
                    <option value="auto">{t("Automatisch erkannt")}</option>
                    {templates.data?.map((tpl: CsvTemplate) => <option key={tpl.id} value={tpl.id}>{t("Vorlage:")} {tpl.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-[minmax(120px,170px)_minmax(0,1fr)_minmax(0,150px)] gap-x-3 gap-y-1.5 items-center text-[13px]">
                  <span className="text-[11.5px] font-medium text-fg-subtle">{t("Feld")}</span>
                  <span className="text-[11.5px] font-medium text-fg-subtle">{t("Spalte in der Datei")}</span>
                  <span className="text-[11.5px] font-medium text-fg-subtle" title={t("Gilt, wenn die Spalte fehlt oder die Zelle leer ist")}>{t("Standard")}</span>
                  {Object.entries(fields).map(([key, f]) => {
                    const defaultOnly = "defaultOnly" in f;
                    const kindOfDefault = DEFAULTABLE[kind][key];
                    const setDefault = (v: string) => { setPreview(null); setDefaults((d) => { const n = { ...d }; if (v) n[key] = v; else delete n[key]; return n; }); };
                    return (
                      <div key={key} className="contents">
                        <span className={cx("truncate", f.required && !mapping[key] && !defaults[key] ? "text-danger font-medium" : "")}>{t(f.label)}{f.required && " *"}</span>
                        {defaultOnly ? (
                          <span className="text-[12px] text-fg-subtle">{t("nur als Standard – gilt, wenn die Datei keine Versandart nennt")}</span>
                        ) : (
                          <select value={mapping[key] ?? ""} onChange={(e) => { setPreview(null); setMapping((m) => { const n = { ...m }; if (e.target.value) n[key] = e.target.value; else delete n[key]; return n; }); }} className="h-8 min-w-0 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label={t("Spalte für {label}", { label: f.label })}>
                            <option value="">{t("— nicht übernehmen —")}</option>
                            {header.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        )}
                        {!kindOfDefault ? <span /> : kindOfDefault === "method" ? (
                          <ShippingMethodSelect value={defaults[key]} onChange={(id) => setDefault(id ?? "")} emptyLabel="—" className="h-8 text-[12.5px]" ariaLabel={t("Standard für {label}", { label: f.label })} />
                        ) : kindOfDefault === "yesno" ? (
                          <select value={defaults[key] ?? ""} onChange={(e) => setDefault(e.target.value)} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label={t("Standard für {label}", { label: f.label })}>
                            <option value="">—</option><option value="ja">{t("ja")}</option><option value="nein">{t("nein")}</option>
                          </select>
                        ) : (
                          <Input value={defaults[key] ?? ""} onChange={(e) => setDefault(kindOfDefault === "country" ? e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2) : e.target.value)} placeholder={kindOfDefault === "country" ? t("DE") : "—"} inputMode={kindOfDefault === "number" ? "decimal" : undefined} className="h-8" aria-label={t("Standard für {label}", { label: f.label })} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {kind === "products" && (
                  <label className="mt-3 flex items-center gap-2 text-[13px] cursor-pointer">
                    <input type="checkbox" className="accent-ink" checked={transfer} onChange={(e) => setTransfer(e.target.checked)} />
                    {t("Neue, vollständige Artikel direkt ans Lager übertragen")}
                  </label>
                )}
                <label className="mt-3 flex items-center gap-2 text-[13px]">
                  <Save className="size-3.5 text-fg-subtle" />
                  <span className="shrink-0">{t("Zuordnung als Vorlage speichern")}</span>
                  <Input value={saveAs} onChange={(e) => setSaveAs(e.target.value)} placeholder={t("Name, z. B. ERP-Export (optional)")} className="flex-1" />
                </label>
              </div>
            )}

            {/* 3. Vorschau */}
            {p && (
              <div className="rounded-xl border border-border p-4 space-y-3 animate-fade-in">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
                  <span className="flex items-center gap-1.5 font-medium"><CheckCircle2 className="size-4 text-success" /> {fmtNumber(count)} {LABEL[kind].what} {t("bereit")}</span>
                  {"orders" in p && <span className="text-fg-muted">{fmtNumber(p.lines)} {t("Positionen")}</span>}
                  {"create" in p && <span className="text-fg-muted">{fmtNumber(p.create)} {t("neu ·")} {fmtNumber(p.update)} {t("ergänzt")}</span>}
                  {"duplicates" in p && p.duplicates.length > 0 && <Badge>{p.duplicates.length} {t("schon importiert")}</Badge>}
                  {p.issueCount > 0 && <Badge tone="warning">{p.issueCount} {t("Hinweise")}</Badge>}
                </div>
                {"unknownSkus" in p && p.unknownSkus.length > 0 && (
                  <p className="text-[12.5px] flex items-start gap-1.5"><AlertTriangle className="size-3.5 text-warning mt-0.5 shrink-0" /> {t("Unbekannte SKU:")} {p.unknownSkus.slice(0, 10).join(", ")}{p.unknownSkus.length > 10 ? " …" : ""} {t("– betroffene Aufträge werden angelegt und zurückgehalten, bis der Artikel existiert.")}</p>
                )}
                {p.issues.length > 0 && (
                  <ul className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border text-[12.5px]">
                    {p.issues.map((i, n) => <li key={n} className="px-3 py-1.5">{i.line ? <span className="tabular-nums text-fg-subtle mr-2">{t("Zeile")} {i.line}</span> : null}{serverText(i.message)}</li>)}
                  </ul>
                )}
                {p.sample.length > 0 && (
                  <table className="w-full text-[12.5px]">
                    <tbody>
                      {"orders" in p
                        ? (p as OrderCsvPreview).sample.map((s) => (
                            <tr key={s.orderNumber} className="border-t border-border"><td className="py-1 font-medium tabular-nums">{s.orderNumber}</td><td>{s.name}</td><td className="text-fg-muted">{s.city} · {s.country}</td><td className="text-fg-muted">{s.shippingMethod}</td><td className="font-mono text-[11.5px]">{s.lines}</td></tr>
                          ))
                        : (p as ProductCsvPreview).sample.map((s) => (
                            <tr key={s.sku} className="border-t border-border"><td className="py-1 font-mono">{s.sku}</td><td>{s.isNew ? <Badge tone="info">{t("neu")}</Badge> : <Badge>{t("ergänzt")}</Badge>}</td><td className="text-fg-muted">{s.fields.join(", ")}</td></tr>
                          ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <div className="px-6 h-16 border-t border-border flex items-center gap-3">
            {missingRequired.length > 0 && file ? (
              <span className="text-[12.5px] text-danger truncate">{t("Zuordnen:")} {missingRequired.join(", ")}</span>
            ) : (
              <span className="text-[12.5px] text-fg-muted">{p ? t("Geprüft – bereit zum Import") : file ? t("Erst prüfen, dann importieren") : ""}</span>
            )}
            <div className="ml-auto flex gap-2">
              <Button disabled={!file || missingRequired.length > 0} loading={check.isPending} onClick={() => check.mutate()}>{t("Prüfen")}</Button>
              <Button variant="primary" disabled={!p || count === 0} loading={run.isPending} onClick={() => run.mutate()}>{p ? t("{count} importieren", { count: fmtNumber(count) }) : t("Importieren")}</Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
